import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../shared/offer-domain/improvement';
import { SubmitConfirmModal } from '../../../../../shared/offer-domain/ui';
import useSetDyedUndyedImprovement from '../../../hooks/useSetDyedUndyedImprovement';
import useSubmitImprovement from '../../../hooks/useSubmitImprovement';
import { OfferTable } from '../../../te-bridge';

const Improvement = ({
  procurement,
  procId,
  parcels = [],
  isLoadingParcels = false,
  isLoadingProcurement = false,
  idOffer: idOfferProp = null,
  isLoadingIdentity: isLoadingIdentityProp = false,
}) => {
  const { saveAlphaImprovement } = useSetDyedUndyedImprovement();
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const idOffer = idOfferProp;
  const isLoadingIdentity = isLoadingIdentityProp;
  const { submitImprovement, isSubmitting } = useSubmitImprovement();

  const handleImprovementMeta = useCallback(
    ({ deliveryDetails: rows } = {}) => {
      setDeliveryDetails(Array.isArray(rows) ? rows : []);
    },
    []
  );

  const closingDate = useMemo(
    () => resolveImprovementClosingDate(deliveryDetails),
    [deliveryDetails]
  );
  const isImprovementOpen = isImprovementClosingActive(closingDate);

  const handleOpenConfirm = () => {
    if (!idOffer) {
      toast.error('Offer ID is required.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const success = await submitImprovement({ idOffer });
    if (success) setConfirmOpen(false);
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted mt-2'>Loading improvement data...</p>
      </div>
    );
  }

  const isResolvingIdentity = isLoadingProcurement || isLoadingIdentity;

  return (
    <>
      <Card className='border shadow-md mb-4'>
        <CardBody>
          <div className='mb-4'>
            <h5 className='fw-bold text-dark mb-1'>Improvement Detail</h5>
            <p className='text-muted small mb-0'>
              List of offers that have been requested for improvement.
            </p>
            {isImprovementOpen ? (
              <Alert color='success' className='mb-0 mt-3'>
                <h6 className='alert-heading mb-1 fw-semibold'>
                  Improvement is in progress
                </h6>
                <p className='mb-0 fs-13'>
                  Improvement is currently in progress and will end on{' '}
                  {formatImprovementClosingDate(closingDate)}.
                </p>
              </Alert>
            ) : (
              <Alert color='danger' className='mb-0 mt-3'>
                <h6 className='alert-heading mb-1 fw-semibold'>
                  Improvement has ended
                </h6>
                <p className='mb-0 fs-13'>
                  Improvement has ended
                  {closingDate
                    ? `. It ended on ${formatImprovementClosingDate(closingDate)}.`
                    : '.'}
                </p>
              </Alert>
            )}
          </div>

          <Row className='mt-3'>
            <Col md={12}>
              <OfferTable
                viewMode='improvement'
                procurementId={procurement?.id || procId}
                procurement={procurement}
                parcels={parcels}
                isLoadingParcels={isLoadingParcels}
                alphaImprovementEditable={isImprovementOpen}
                onAlphaImprovementChange={
                  isImprovementOpen ? saveAlphaImprovement : undefined
                }
                onImprovementMeta={handleImprovementMeta}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {isImprovementOpen && (
        <div className='req-actionbar d-print-none'>
          <Button
            type='button'
            color='primary'
            className='d-flex align-items-center gap-2'
            disabled={isSubmitting || isResolvingIdentity || !idOffer}
            onClick={handleOpenConfirm}
          >
            {isSubmitting ? (
              <Spinner size='sm' />
            ) : (
              <i className='bx bx-send'></i>
            )}
            <span>Submit Improvement</span>
          </Button>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title='Submit Improvement'
        message='You are about to submit your improvement. After submission, your improvement responses will be sent to the evaluator and this section may become view-only until further action.'
        confirmLabel='Submit'
        cancelLabel='Back'
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default Improvement;
